import React, { useEffect, useState, useImperativeHandle } from 'react'
import { connect } from 'react-redux'
import { FormattedMessage } from 'react-intl';
import {
  useTable,
  useSortBy,
  useFlexLayout,
  // useBlockLayout,
  useResizeColumns,
  useRowSelect,
  useExpanded
} from 'react-table'
import Toast from '../Toast/Toast';
import MesColumnToggleButton from '../MessageManager/MesColumnToggleButton';
import Searchbar from '../Searchbar/Searchbar';
import SubTable from './SubTable';
import DragIcon from '../../assets/icons/table/drag.svg';
import ArrowDownIcon from '../../assets/icons/table/arrow-down.svg';
import CellWithTooltip from './CellWithTooltip';
import tableModal from '../../utils/tableModal';
import { rowFilter } from '../../utils/tableFilter';
import commonFunctions from '../../utils/commonFunctions';
import { setUserState } from '../../store/user';
import './Table.css'
import '../Modals/Modals.css'
import './Toolbar.css'
import './MesTable.css'
import '../../pages/MessageManager/Messenger.css'

function MesTable(props) {
  const {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey = null,
    pageType,
    modalId = null,
    columns,
    data, setData,
    dataMap,
    dataLoaded,
    // Data Handling props
    generateDataSlice,
    setDataCount,
    sortMap,
    // Table props
    success, setSuccess,
    error, setError,
    setSortBy,
    sortByCol,
    sortByAsc,
    dataCount,
    totalDataCount,
    selectedIds,
    startIndex = null,
    totalSize = null,
    selectedSize = null,
    // Select Row Props
    setSelectedIds,
    // Page function props
    handleEditClick = null,
    handleSingleDelete = null,
    subTableProps = null,
    tableRef,
    tableWrapperRef,
    searchValue,
    setSearchValue,
    simpleTable = false,
    // Checkbox props
    singleSelect = false,
    // Table header buttons
    initialContainerHeight = 'calc(100vh - 400px)',
    buttons: {
      titleButtons = null,
      toolbarLeftButtons = null,
      toolbarRightButtons = null,
      footerButtons = null,
    } = {},
    user,
    // Custom props
    hasFooter = true,
  } = props;

  const ROW_HEIGHT = 48;

  const [containerHeight, setContainerHeight] = useState(initialContainerHeight);
  const [lastSearchValue, setLastSearchValue] = useState('');
  const [dataStatus, setDataStatus] = useState('');
  const [lastClickedRow, setLastClickedRow] = useState(null);
  const [hasCheckbox, setHasCheckbox] = useState(false);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const filter = (column) => {
      let flag  = column.isVisible === undefined || column.isVisible === true;
      if (user && user.preferences && 
          user.preferences.hiddenColumns &&
          user.preferences.hiddenColumns[tableKey]) {
        flag = flag && !user.preferences.hiddenColumns[tableKey].includes(column.id);
      }
      return flag;
    };
    return columns.filter(filter).map((column) => column.id);
  });

  const [columnsNew] = useState(() => {
    return columns.map((column) => {
      // Set the column width to the saved value, or a default width of 150 if no saved value exists
      column.width = tableModal.loadColumnWidth(pageType, column.id, column.width ? column.width : undefined);
      return column;
    });
  });

  const [expandeds, setExpandeds] = useState('');

  // Search on interval
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (searchValue !== lastSearchValue) {
        setLastSearchValue(searchValue);
        search();
      }
    }, 500);
    return () => {
      window.clearInterval(timer);
    };
  });

  useImperativeHandle(tableRef, () => ({
    search,
  }))

  /**
   * Filters data based on searchValue and updates the data state
   * @returns 
   */
  const search = (dataMapTem = dataMap, startIndex = 0) => {
    console.log("doing a search")
    const text = searchValue;

    // Extract search accessors from columns
    const searchAccessors = columns
      .filter(col => col.isVisible && col.accessor) // Filter columns that are visible and have an accessor
      .map(col => col.accessor); // Get the accessor for each column

    const filteredIds = dataMapTem.filteredIds.filter((id) => {
      const rowData = dataMapTem.data[id];
      return rowFilter(rowData, text, searchAccessors); // Pass the search accessors to the rowFilter
    });

    const sortedIds = sortMap(dataMapTem, filteredIds); // Sort the filtered IDs
    setDataCount(sortedIds.length);
    setData(generateDataSlice(dataMapTem, startIndex > dataCount ? dataCount : startIndex));
  };

  /**
   * Toggles the visibility of a column
   * @param {*} columnId 
   */
  const toggleColumn = (columnId) => {
    const cols = visibleColumns.includes(columnId) ? visibleColumns.filter((id) => id !== columnId): [...visibleColumns, columnId];
    setVisibleColumns(cols);
    
    // Process and save the user's hide column preferences
    commonFunctions.processHideColumnPreferences(tableKey, user, columns, cols, props, setUserState);
  };

  /**
   * Selects All items
   */
  const selectAll = function () {
    let keys = dataMap.filteredIds;
    let sortedKeys = dataMap.sortedIds;

    // If searchValue is not empty, select all items in the search results and return
    if (searchValue) {
      sortedKeys.forEach((key) => {
        dataMap.data[key].checked = true;
        dataMap.selectedIds[key] = 1; // set to 1 for no particular reason just to insert it into map
      });
    } else {
      // Iterate over all items and set their 'checked' state to true
      keys.forEach((key) => {
        dataMap.data[key].checked = true;
        dataMap.selectedIds[key] = 1; // set to 1 for no particular reason just to insert it into map
      });
    }

    // Copy data from our dataMap into React state
    setData(generateDataSlice(dataMap, startIndex));
    setSelectedIds({...dataMap.selectedIds});
  };

  /**
   * Deselect all items
   */
  const deselectAll = function () {
    let keys = Object.keys(dataMap.data);

    // Iterate over all items and set their 'checked' state to false
    keys.forEach((key) => {
      dataMap.data[key].checked = false;
    })

    // Set selectedIds as empty
    dataMap.selectedIds = {};

    setData(generateDataSlice(dataMap, startIndex));
    setSelectedIds({...dataMap.selectedIds});
  }

  // Initialize a variable to track shift-click behavior
  const [isInitialClick, setIsInitialClick] = useState(true);

  /**
   * Handle shift-click row selection
   * @param {Object} row - Row object
   */
  const handleShiftClick = (row) => {
    const lastClickedRowId = lastClickedRow.original.id;
    const clickedRowId = row.original.id;
    const sortedIds = dataMap.sortedIds;
    const lastClickedRowIndex = sortedIds.findIndex((id) => id === lastClickedRowId);
    const clickedRowIndex = sortedIds.findIndex((id) => id === clickedRowId);
    const start = Math.min(lastClickedRowIndex, clickedRowIndex);
    const end = Math.max(lastClickedRowIndex, clickedRowIndex);
    const isShiftClickUpwards = clickedRowIndex < lastClickedRowIndex;
    const isLastClickedRowSelected = dataMap.selectedIds[lastClickedRowId] === 1;
    const updatedSelectedIds = { ...dataMap.selectedIds };

    console.log('start', start, 'end', end)

    for (let i = start; i <= end; i++) {
      const currentRowId = sortedIds[i];
      if (isShiftClickUpwards && isInitialClick && i === lastClickedRowIndex) {
        // Skip toggling the last clicked row during upwards initial shift-click
        continue;
      }
      if (!isShiftClickUpwards && isInitialClick && i === start && isLastClickedRowSelected) {
        // Skip toggling the start row if it's initially selected during downwards initial shift-click
        continue;
      }
      if (isShiftClickUpwards && !isInitialClick && i === lastClickedRowIndex && !isLastClickedRowSelected) {
        // Skip toggling the last clicked row during consecutive upwards shift-clicks if it's not selected
        continue;
      }
      if (!isShiftClickUpwards && !isInitialClick && i === start && isLastClickedRowSelected) {
        // Skip toggling the start row during consecutive downwards shift-clicks if it's selected
        continue;
      }

      // Update the 'checked' property of the data
      dataMap.data[currentRowId].checked = updatedSelectedIds[currentRowId] !== 1;

      if (updatedSelectedIds[currentRowId] === 1) {
        // If the row was previously selected, remove it from the selectedIds
        delete updatedSelectedIds[currentRowId];
      } else {
        updatedSelectedIds[currentRowId] = 1;
      }
    }
    setIsInitialClick(false);
    dataMap.selectedIds = updatedSelectedIds;
  };

  /**
   * Selects the row
   * @param {*} e - Event
   * @param {*} row - Row object
   * @param {*} value - Value of the checkbox
   */
  const selectRow = (e, row, value) => {
    if (lastClickedRow && e.shiftKey && !singleSelect) {
      handleShiftClick(row);
    } else {
      if (singleSelect) {
        // In single select mode, clear all previous selections
        deselectAll();
        dataMap.selectedIds[row.original.id] = 1;  // Only one row selected at a time
      } else {
        // Normal multi-selection behavior
        if (value === false) {
          dataMap.data[row.original.id].checked = true;
          dataMap.selectedIds[row.original.id] = 1;
        } else if (value === true) {
          dataMap.data[row.original.id].checked = false;
          delete dataMap.selectedIds[row.original.id];
        }
      }
    }
    setLastClickedRow(row);
    setData(generateDataSlice(dataMap, startIndex));
    setSelectedIds({...dataMap.selectedIds});
  };

  /**
   * Handle clicking on expander button
   * @param {*} e 
   * @param {*} row 
   */
  const handleExpanderClick = (e, row) => {
    e.preventDefault();
    if (row.isExpanded) {
      let newList = expandeds.filter((item) => item !== row.original.id);
      setExpandeds(newList);
    } else {
      setExpandeds([...expandeds, row.original.id]);
    }
  }

  /**
   * Resizes the table
   * @param {*} event 
   */
  const handleMouseDown = (event) => {
    event.preventDefault();
    const startHeight = tableWrapperRef.current.getBoundingClientRect().height;
    const startY = event.pageY;

    // Set the maximum height to the window height minus 280px
    const maxHeight = window.innerHeight - 280;

    const handleMouseMove = (moveEvent) => {
      let newHeight = startHeight + (moveEvent.pageY - startY);
      newHeight = Math.min(newHeight, maxHeight);
      setContainerHeight(`${newHeight}px`);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Gets the class name for the cell (checkbox or expander)
   * @param {*} columnId 
   * @returns 
   */
  const getCellClassName = (columnId, maxWidth = "") => {
    switch (columnId) {
      case 'checkbox': return 'checkboxCell';
      case 'expander': return 'expanderCell';
      case 'actions': return 'actionsCell ' + maxWidth;
      default: return undefined;
    }
  };

  /**
   * Renders a row in the table
   * @param {*} row - the row to render
   * @param {*} cellRenderer - the cell renderer to use 
   * @returns Clickable row if the modalId prop is passed, otherwise a normal row
   */
  const renderRow = ({ row, cellRenderer }) => {
    prepareRow(row);
    const rowProps = row.getRowProps({ style: { maxHeight: ROW_HEIGHT } });
    const { key: rowKey, ...restRowProps } = rowProps;
    
    // Check if this row is selected
    const isSelected = dataMap.selectedIds[row.original.id] === 1;

    return (
      <tr 
        key={rowKey} 
        {...restRowProps} 
        className={isSelected ? 'selected-row' : ''}
      >
        {row.cells.map((cell) => {
          if (visibleColumns.includes(cell.column.id)) {
            const onClickHandler = (e) => {
              switch (cell.column.id) {
                case "checkbox":
                  return;
                case "expander":
                  return e.target.nodeName === 'SPAN' ? handleExpanderClick(e, row) : undefined;
                case "actions":
                  if (e.target.classList.contains('editAction')) {
                    return handleEditClick ? handleEditClick(e, row.original.id) : undefined;
                  }
                  if (e.target.classList.contains('deleteAction')) {
                    return handleSingleDelete ? handleSingleDelete(e, row.original.id) : undefined;
                  }
                  return;
                default:
                  return;
              }
            };

            const cellProps = cell.getCellProps({
              style: { height: ROW_HEIGHT }
            });
            const { key: cellKey, ...restCellProps } = cellProps;

            const cellClassName = getCellClassName(cell.column.id, cell.column.mWidth);
            return (
              <td
                key={cellKey}
                className={`mesCustomBodyCell ${cellClassName}`}
                onClick={onClickHandler}
                {...restCellProps}
              >
                {cell.column.id === 'icon' &&
                  <img className='customImgCell' src={cell.value} alt={cell.value} />
                }
                {cell.column.id !== 'icon' &&
                  <CellWithTooltip>
                    {cellRenderer(cell)}
                  </CellWithTooltip>
                }
              </td>
            );
          }
          return null;
        })}
      </tr>
    );
  };

  const renderHeader = (headerGroup) => {
    return headerGroup.headers.map((column) => {
      if (visibleColumns.includes(column.id)) {
        const headerClassName = getCellClassName(column.id, column.mWidth);
        const isSpecialColumn = ['checkbox', 'expander', 'actions', 'viewReceivers'].includes(column.id);

        // Extract key from header props
        const { key, ...restHeaderProps } = column.getHeaderProps();
        if (column.id === 'checkbox' && !hasCheckbox) {
          setHasCheckbox(true);
        }
        return (
          <th
            key={key}
            {...restHeaderProps}
            className={`mesCustomHeaderCell ${headerClassName} d-flex align-items-center`}
            onClick={(e) => {
              if (!isSpecialColumn && setSortBy && !simpleTable) {
                e.preventDefault();
                setSortBy(column.id);
              }
            }}
          >
            {singleSelect && column.id === 'checkbox' ? (
              <div style={{ width: '100%' }} />
            ) : (
              isSpecialColumn ? column.render('Header') : <FormattedMessage id={column.render('Header')} />
            )}

            {!simpleTable && !isSpecialColumn && (
              <>
                <span style={{ marginLeft: 'auto' }}>
                  {column.id === sortByCol ? (
                    !sortByAsc ? (
                      <img id="sortIconDown" className="sort-icon down" style={{ marginLeft: '2px' }} src={ArrowDownIcon} alt="Arrow Down" />
                    ) : (
                      <img id="sortIconUp" className="sort-icon up" style={{ marginLeft: '2px' }} src={ArrowDownIcon} alt="Arrow Down" />
                    )
                  ) : null}
                </span>
                <div
                  {...column.isResizing && tableModal.updateColumnWidths(pageType, column)}
                  {...column.getResizerProps()}
                  className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={DragIcon} className="drag-icon" draggable="false" alt="Drag Icon" />
                </div>
              </>
            )}
          </th>
        );
      }
      return null;
    });
  };

  const defaultColumn = React.useMemo(
    () => ({

    }),
    []
  )

  // Effect to update hiddenColumns when visibleColumns changes
  useEffect(() => {
      const hiddenColumns = columnsNew
          .filter((column) => !visibleColumns.includes(column.id))
          .map((column) => column.id);
      setHiddenColumns(hiddenColumns);
  }, [visibleColumns, columnsNew]);

  // Initializing useTable hooks
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
    setHiddenColumns,
  } = useTable(
    {
      columns: columnsNew,
      data,
      defaultColumn,
      initialState: {
        hiddenColumns: columnsNew.filter((column) => !column.isVisible).map((column) => column.id),
      },
      selectRow,
      selectAll,
      deselectAll,
      dataCount,
      totalDataCount,
      selectedIds,
      sortByCol,
      setSortBy,
      startIndex,
      setSearchValue,
      visibleColumns,
      autoResetExpanded: false,
    },
    // useBlockLayout,
    useFlexLayout,
    useResizeColumns,
    useSortBy,
    useExpanded,
    useRowSelect,
  )

  // Handle data status message
  useEffect(() => {
    // Check if data is loaded
    if (dataLoaded) {
      // Data is loaded, check for data count
      if (dataCount === 0) {
        setDataStatus('NO_DATA_FOUND');
      } else {
        setDataStatus('DATA_LOADED');
      }
    } else {
      setDataStatus('DATA_IS_LOADING');
    }
  }, [dataLoaded, dataCount]);

  return (
    <div className="tableWrapper mesTableWrapper ">
      {(modalId !=="GroupManagement" && modalId !== "MediaLibrary" && !simpleTable) && (<div className="mes-title d-flex justify-content-between align-items-center">
        <FormattedMessage id={pageType} />
      </div>)}
      <div className="tableContainer">
        {/* Toolbar */}
        <div id="toolbar">
          <div id="toolbar-top" className="toolbar-top row">
            <div className="col d-flex justify-content-start">
                {toolbarLeftButtons}
                {!simpleTable && <MesColumnToggleButton columns={columns} visibleColumns={visibleColumns} toggleColumn={toggleColumn} />}
              <Searchbar search={setSearchValue} placeholder={modalId === 'Status' ? 'SEARCH_MESSAGE' : 'SEARCH'} />
            </div>
            <div className="col d-flex justify-content-end">
              {toolbarRightButtons}
            </div>
          </div>
          {footerButtons &&
            <div id="toolbar-bottom" className="row">
              <div className="col d-flex justify-content-start">
                <div className="selected-count fw-normal">
                  {Object.keys(selectedIds).length} <FormattedMessage id="SELECTED_TEXT" defaultMessage="items selected" />
                </div>
                {footerButtons}
              </div>
            </div>
          }
        </div>
        <div className="mesScrollBarWrapper" id="mesScrollBarWrapper" ref={tableWrapperRef} style={{ maxHeight: containerHeight }}>
          <table className="table mestable" id="mesMainTable" key={pageType} {...getTableProps()}>
            <thead>
              <tr>
                {headerGroups.map((headerGroup) => renderHeader(headerGroup))}
              </tr>
            </thead>
            <tbody>
              <tr id='top-block' style={{ borderWidth: 0, height: (startIndex > dataCount ? dataCount : startIndex) * ROW_HEIGHT, overflow: 'hidden' }}>
                <td style={{ overflow: 'hidden', borderWidth: 0, padding: 0 }} />
              </tr>
              {data.length === 0 ? (
                <tr>
                  <td className="fullWidthCell" colSpan={Math.max(visibleColumns.length, 1)} style={{ borderWidth: 0, padding: 0 }}>
                    <div className="data-status text-center">
                      <h3 className="fw-bold">{dataStatus !== "" ? <FormattedMessage id={dataStatus} /> : ""}</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  prepareRow(row);
                  let canExpanded = expandeds.includes(row.original.id);
                  // if isExpanded is defined, then we will use it to expand/collapse the row. Otherwise, we will use the expandedsRef value.
                  let canToggle = row.isExpanded === undefined || row.isExpanded === false ? canExpanded : !canExpanded
                  if (canToggle)
                    row.toggleRowExpanded(canExpanded)
                  return (
                    <React.Fragment key={row.id}>
                      {renderRow({ row, cellRenderer: (cell) => cell.render('Cell') })}
                      {row.isExpanded && (
                        <tr><td colSpan={visibleColumns.length} style={{ padding: 0 }}>
                          <SubTable {...subTableProps} row={row} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
              <tr id='bottom-block' style={{ borderWidth: 0, height: (dataCount - data.length - (startIndex > dataCount ? dataCount : startIndex)) * ROW_HEIGHT, overflow: 'hidden' }}>
                <td style={{ overflow: 'hidden', borderWidth: 0, padding: 0 }} />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="resize-handle" onMouseDown={handleMouseDown}>
          <div></div>
        </div>
      </div>
      { !simpleTable && <div className="tableFooter">
        <FormattedMessage id="CURRENTLY_VIEWING" values={{ dataCount: dataCount }} />&nbsp;&nbsp;|&nbsp;&nbsp;
        <FormattedMessage id="TOTAL_COUNT" values={{ totalDataCount: totalDataCount }} />
        { hasCheckbox && <> &nbsp;&nbsp;|&nbsp;&nbsp; <FormattedMessage id="SELECTED" values={{ selectedDataCount: Object.keys(selectedIds).length }} /></> }
        {selectedSize && (
          <>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <FormattedMessage id="SELECTED_SIZE" values={{ selectedSize: selectedSize }} />
          </>
        )}
        {totalSize && (
          <>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <FormattedMessage id="TOTAL_SIZE" values={{ totalSize: totalSize }} />
          </>
        )}
      </div>}
      <Toast
        success={success}
        error={error}
        setSuccess={setSuccess}
        setError={setError}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(MesTable)